import React, { useEffect, useRef, useState } from 'react';
import DataTable from 'react-data-table-component';
import moment from 'moment';
import { Chip, Tooltip } from '@mui/material';
import { AiOutlineVerticalAlignBottom } from 'react-icons/ai';
import { useSelector } from 'react-redux';
import { getHoldingSettlements } from '../../helper/Api';
import {
    AddCommasToAmount,
    downloadSafeFile,
    getSafeDocumentUrl,
} from '../../helper/Utils';
import './HoldingSettlementsList.css';

const TABLE_HEADER_HEIGHT = 56;
/** Chip + action buttons need more than default RDT row height */
const TABLE_ROW_HEIGHT = 64;
const TABLE_PAGINATION_HEIGHT = 56;
const TABLE_CARD_PADDING = 32;
const TABLE_BOTTOM_GAP = 16;

/** Accept array / nested list / single-object / PHP-style map payloads from BE */
const extractSettlementList = (payload) => {
    if (!payload) return [];

    const asRows = (value) => {
        if (!value) return null;
        if (Array.isArray(value)) return value.filter(Boolean);
        if (typeof value !== 'object') return null;

        // Single settlement object
        if (value.id != null || value.settlement_date != null || value.account_no != null) {
            return [value];
        }

        // PHP associative list encoded as {"0": {...}, "1": {...}}
        const values = Object.values(value);
        if (
            values.length > 0 &&
            values.every((item) => item && typeof item === 'object' && !Array.isArray(item))
        ) {
            return values.filter(Boolean);
        }
        return null;
    };

    for (const candidate of [
        payload.result,
        payload.data,
        payload.list,
        payload.result?.data,
        payload.result?.list,
        payload.data?.data,
        payload.data?.list,
    ]) {
        const rows = asRows(candidate);
        if (rows) return rows;
    }
    return [];
};

const YesPendingCell = ({ value }) => {
    const isYes = String(value) === '1';
    return (
        <span
            className={`holding-settlements-status-pill ${
                isYes
                    ? 'holding-settlements-status-pill--yes'
                    : 'holding-settlements-status-pill--pending'
            }`}
        >
            {isYes ? 'Yes' : 'Pending'}
        </span>
    );
};

/** Prefer safe https URL; fall back through known BE field names. */
const resolveDocUrl = (...candidates) => {
    for (const value of candidates) {
        const safe = getSafeDocumentUrl(value);
        if (safe) return safe;
    }
    return '';
};

const getStatusChip = (status) => {
    const normalized = String(status || '').toLowerCase().trim();

    if (normalized === 'onhold' || normalized === 'on hold') {
        return <Chip label="On Hold" color="warning" size="small" />;
    }
    if (normalized === 'completed') {
        return <Chip label="Sent to Bank" color="success" size="small" />;
    }
    if (normalized === 'settled') {
        return <Chip label="Completed" color="secondary" size="small" />;
    }
    if (normalized === 'in progress' || normalized === 'in_progress' || normalized === 'inprogress') {
        return <Chip label="In Progress" color="primary" size="small" />;
    }

    return <Chip label="In Queue" color="primary" size="small" />;
};

const HoldingSettlementsList = () => {
    const tableContainerRef = useRef(null);
    const { authData } = useSelector((state) => state.authData);
    const departmentId = authData?.user?.department_id;
    const [loading, setLoading] = useState(false);
    const [rowsPerPage, setRowsPerPage] = useState(100);
    const [records, setRecords] = useState([]);
    const [tableScrollHeight, setTableScrollHeight] = useState('400px');

    const customStyles = {
        headCells: {
            style: {
                fontWeight: 'bold',
                fontSize: '14px',
                backgroundColor: '#f8f9fc',
            },
        },
        cells: {
            style: {
                fontSize: '13px',
            },
        },
    };

    const handleDownload = (url) => {
        if (!url) return;
        downloadSafeFile(url);
    };

    const columns = [
        {
            name: 'Unique ID',
            selector: (row) => row.id,
            width: '150px',
        },
        {
            name: 'Sett. Date',
            selector: (row) => (row.settlement_date
                ? moment(row.settlement_date, 'YYYY-MM-DD').format('DD-MM-YYYY')
                : ''),
            width: '150px',
        },
        {
            name: 'Status',
            cell: (row) => getStatusChip(row.status),
            center: true,
            width: '130px',
        },
        {
            name: 'Amount',
            cell: (row) => (
                <span className="holding-settlements-amount">
                    {row.amount !== null && row.amount !== undefined && row.amount !== ''
                        ? AddCommasToAmount(row.amount)
                        : ''}
                </span>
            ),
            right: true,
            width: '130px',
        },
        {
            // ACH debit / sponsor = PPA-limit account on settlement (see FE_HOLDING_SETTLEMENT.md)
            name: 'Debit Account',
            selector: (row) => row.account_no,
            wrap: true,
            minWidth: '150px',
        },
        {
            name: 'Mail Sent',
            cell: (row) => <YesPendingCell value={row.is_mail_sent} />,
            center: true,
            width: '110px',
        },
        {
            name: 'File Shared',
            cell: (row) => <YesPendingCell value={row.is_file_shared} />,
            center: true,
            width: '110px',
        },
        {
            name: 'Actions',
            cell: (row) => {
                // Control Excel → mail_file_path; ACH txt → sftp_file_url
                const mailUrl = resolveDocUrl(
                    row.mail_file_path,
                    row.mail_file_url
                );
                const sftpUrl = resolveDocUrl(
                    row.sftp_file_url,
                    row.file_url
                );

                return (
                    <div className="holding-settlements-actions">
                        <Tooltip title="Download Control Excel (mail)" arrow placement="top">
                            <span className="holding-settlements-action-tooltip-wrap">
                                <button
                                    type="button"
                                    className="holding-settlements-action-btn holding-settlements-action-btn--mail"
                                    disabled={!mailUrl}
                                    onClick={() => handleDownload(mailUrl)}
                                >
                                    <AiOutlineVerticalAlignBottom size={16} />
                                </button>
                            </span>
                        </Tooltip>
                        <Tooltip title="Download ACH / SFTP file" arrow placement="top">
                            <span className="holding-settlements-action-tooltip-wrap">
                                <button
                                    type="button"
                                    className="holding-settlements-action-btn holding-settlements-action-btn--file"
                                    disabled={!sftpUrl}
                                    onClick={() => handleDownload(sftpUrl)}
                                >
                                    <AiOutlineVerticalAlignBottom size={16} />
                                </button>
                            </span>
                        </Tooltip>
                    </div>
                );
            },
            center: true,
            width: '110px',
        },
        {
            name: 'Created At',
            selector: (row) => (row.created_at
                ? moment(row.created_at, ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601]).format('DD-MM-YYYY hh:mm A')
                : ''),
            wrap: true,
            minWidth: '170px',
        },
    ];

    const fetchSettlements = async () => {
        try {
            setLoading(true);
            const response = await getHoldingSettlements({
                department_id: departmentId,
            });
            if (response?.data?.status) {
                const list = extractSettlementList(response.data);
                setRecords(
                    list.map((row, index) => ({
                        ...row,
                        rowKey: row?.id != null ? String(row.id) : `settlement-${index}`,
                    }))
                );
            } else {
                setRecords([]);
            }
        } catch (error) {
            setRecords([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettlements();
    }, [departmentId]);

    useEffect(() => {
        const updateTableHeight = () => {
            const rowCount = Math.max(records.length, 1);
            const visibleRows = Math.min(rowCount, rowsPerPage);
            const bodyContentHeight = visibleRows * TABLE_ROW_HEIGHT + 16;
            const containerTop =
                tableContainerRef.current?.getBoundingClientRect().top ?? 220;
            const maxBodyHeight =
                window.innerHeight -
                containerTop -
                TABLE_HEADER_HEIGHT -
                TABLE_PAGINATION_HEIGHT -
                TABLE_CARD_PADDING -
                TABLE_BOTTOM_GAP;
            const viewportBody = Math.max(maxBodyHeight, 200);

            // Content fits → use content height (no giant empty body).
            // Content overflows → cap to viewport for internal scroll.
            setTableScrollHeight(
                `${Math.min(bodyContentHeight, viewportBody)}px`
            );
        };

        const raf = window.requestAnimationFrame(updateTableHeight);
        window.addEventListener('resize', updateTableHeight);
        return () => {
            window.cancelAnimationFrame(raf);
            window.removeEventListener('resize', updateTableHeight);
        };
    }, [records.length, rowsPerPage, loading]);

    // fixedHeader with a too-small scroll height clips Chip rows to "blank"
    const useFixedHeader = records.length > 5;

    return (
        <div ref={tableContainerRef} className="holding-settlements-datatable-root card p-3">
            <DataTable
                customStyles={customStyles}
                className="dataTables_wrapper"
                columns={columns}
                data={records}
                keyField="rowKey"
                progressPending={loading}
                persistTableHead
                sortable={false}
                fixedHeader={useFixedHeader}
                fixedHeaderScrollHeight={useFixedHeader ? tableScrollHeight : undefined}
                pagination
                paginationPerPage={rowsPerPage}
                paginationTotalRows={records.length}
                paginationRowsPerPageOptions={[100, 200, 500, 1000]}
                onChangeRowsPerPage={setRowsPerPage}
                noDataComponent={
                    <div className="p-4 text-muted">No holding settlement records found.</div>
                }
            />
        </div>
    );
};

export default HoldingSettlementsList;
